import { cx } from '@linaria/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { MoveFocusInside } from 'react-focus-on';
import { TransitionGroup } from 'react-transition-group';
import { getCoWorkersQuery, getClassesQuery } from '@pm2/api';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { CrossIcon } from '@ui/kit';
import { getSubjectsQuery } from '@utils/constants';
import { CSSRefTransition, IsomorphicPortal } from '@utils/react';
import { useGetAllCoursesQuery, getAllCoursesQuery } from '../../api/query.get-all.js';
import { ErrorMessage } from '../../atoms/error.js';
import { useSelectedCourse } from '../../state/selectors.js';
import { courseBuilderStore } from '../../state/store.js';
import { CoursePage } from './features/course/_course-page.js';
import { Overview } from './features/overview/_overview.js';
import { ShareDrawer } from './features/share-drawer/_share-drawer.js';
import { CourseBuilderPanelNotifier } from './integration.notifier.js';
import { rootFocusOn, routeCls, routeChangeDurationMs, closeButtonCls, panelCls, toastsContainerCls } from './panel.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const panelIdentifier = "course-builder--panel";
const panelSelector = `.${panelIdentifier}`;

/**
 * Renders the course builder panel which includes the required functionality
 * to display, create, edit, delete and share courses
 */
function CourseBuilderPanel() {
  const courses = useGetAllCoursesQuery();
  const selectedCourse = useSelectedCourse();

  // prefetch subjects immediately when the course builder panel is opened
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.prefetchQuery(getSubjectsQuery());
  }, [queryClient]);
  const onClose = () => {
    courseBuilderStore.dispatch(courseBuilderStore.actions.togglePanel(false));
  };
  const contentKey = courses.error ? "error" : selectedCourse ? "course" : "overview";
  let content;
  switch (contentKey) {
    case "overview":
      content = /*#__PURE__*/jsx(Overview, {});
      break;
    case "course":
      content = /*#__PURE__*/jsx(CoursePage
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      , {
        course: selectedCourse
      });
      break;
    case "error":
      content = /*#__PURE__*/jsx(PanelErrorMessage, {});
      break;
  }
  const direction = contentKey === "course" ? "forwards" : "backwards";
  const toastsContainerRef = useRef(null);
  return /*#__PURE__*/jsxs(CourseBuilderPanelNotifier, {
    containers: {
      dialogs: "inline",
      toasts: toastsContainerRef
    },
    children: [/*#__PURE__*/jsx(MoveFocusInside, {
      className: rootFocusOn,
      children: /*#__PURE__*/jsxs("div", {
        className: cx(panelCls, direction, panelIdentifier),
        onClick: evt => evt.stopPropagation(),
        children: [/*#__PURE__*/jsx(TransitionGroup, {
          component: null,
          children: /*#__PURE__*/jsx(CSSRefTransition, {
            timeout: routeChangeDurationMs,
            classNames: routeCls,
            children: ref => /*#__PURE__*/jsx("div", {
              ref: ref,
              className: cx(routeCls, ["overview", "course"].includes(contentKey) && "noScroll"),
              children: /*#__PURE__*/jsx(MoveFocusInside, {
                children: content
              })
            })
          }, contentKey)
        }), /*#__PURE__*/jsx(ShareDrawer, {}), /*#__PURE__*/jsx("button", {
          className: closeButtonCls,
          onClick: onClose,
          "aria-label": "Luk panel",
          children: /*#__PURE__*/jsx(CrossIcon, {
            height: 14
          })
        })]
      })
    }), /*#__PURE__*/jsx(IsomorphicPortal, {
      children: /*#__PURE__*/jsx("div", {
        ref: toastsContainerRef,
        className: toastsContainerCls
      })
    })]
  });
}
function PanelErrorMessage() {
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  const [isLoading, setLoading] = useState(false);
  const client = useQueryClient();
  const handleRetry = async () => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    try {
      await Promise.all([client.fetchQuery(getAllCoursesQuery(user)), client.fetchQuery(getCoWorkersQuery(user)), client.fetchQuery(getClassesQuery(user))]);
    } catch {
      notifier.notify({
        id: "panel.load.error",
        type: "error",
        body: "Noget gik galt under indlæsningen, prøv venligst igen om lidt."
      });
    } finally {
      setLoading(false);
    }
  };
  return /*#__PURE__*/jsx(ErrorMessage, {
    onRetry: handleRetry,
    isLoading: isLoading
  });
}

export { CourseBuilderPanel, panelSelector };
//# sourceMappingURL=panel.js.map
